import { ContentFeedMetadata, ContentType, SectionType } from 'types/publishedAssets';
import { useTranslation } from 'react-i18next';
import { useCallback, useContext, useMemo, useState } from 'react';
import { PageContext } from 'src/context/PageContext';
import { FeedItem } from 'types/feeds';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { BlockHeading } from 'components/Content/Atoms/BlockHeading';
import { SearchBar } from 'components/Atoms/SearchBar/SearchBar';
import styles from './Feed.module.scss';
import { FeedItemsInTagGroup } from './FeedItemsInTagGroup';
import { useParticipationCounts } from './hooks';
import { DoubleColonFilters } from './DoubleColonFilters';
import {
  buildDoubleColonFilterGroups,
  DoubleColonSelection,
  hasActiveFilters,
  matchesDoubleColonSelection,
} from './doubleColonFilterUtils';
import { matchesFeedKeyword } from './feedKeywordSearch';

interface FeedProps {
  content: ContentType;
  section: SectionType;
}

export function Feed(props: FeedProps) {
  const { content, section } = props;
  const { t } = useTranslation();
  const { numberOfColumns } = section.metadata || {};
  const { display_type: displayType } = content;
  const {
    feed_type: feedType,
    group_by_tag: groupByTag,
    doubleColonFilterEnabled,
  } = content.metadata as ContentFeedMetadata;
  const { feeds, urlParams, page } = useContext(PageContext);
  const { translate } = useGetContentTranslations();

  if (!feedType) throw new Error('Missing feed type');

  const getOtherTag = useCallback(() => {
    switch (feedType) {
      case 'project':
        return t('feed.otherProjects');
      case 'hub':
        return t('feed.otherHubs');
      case 'update':
        return t('feed.otherUpdates');
      case 'engagement_activity':
        return t('feed.otherActivities');
      default:
        return '';
    }
  }, [feedType, t]);

  const rawItems = useMemo<FeedItem[]>(() => feeds?.[content.id] || [], [feeds, content.id]);

  const [selection, setSelection] = useState<DoubleColonSelection>({});
  // Owned here rather than per tag group so the keyword search can share the filter row and be
  // applied alongside the selection, giving both the same empty state and the same Clear control.
  const [searchValue, setSearchValue] = useState('');
  // SearchBar seeds its input from `value` once and then manages the text itself, so clearing the
  // state alone would leave the old keyword on screen. Remounting it is how the box is emptied.
  const [searchInstance, setSearchInstance] = useState(0);
  const locale = urlParams?.locale || page?.default_locale_code;

  const clearFilters = () => {
    setSelection({});
    setSearchValue('');
    setSearchInstance((current) => current + 1);
  };

  // Options are derived once from the unfiltered set and are not re-faceted as selections narrow,
  // matching Commonplace. Every item is already loaded server-side, so this never refetches.
  const filterGroups = useMemo(
    () => (doubleColonFilterEnabled ? buildDoubleColonFilterGroups(rawItems, locale) : []),
    [doubleColonFilterEnabled, rawItems, locale],
  );
  // The filter row only exists once the data yields a group. A flagged block whose items carry no
  // delimited tag has no row to host the keyword search, so each tag group keeps its own bar.
  const hasFilterRow = filterGroups.length > 0;

  // Selection and keyword are applied together here, so the filtered-to-zero empty state below
  // covers a keyword that matches nothing just as it covers a selection that does.
  const filteredItems = useMemo(() => {
    if (!hasFilterRow) return rawItems;
    return rawItems.filter(
      (item) => matchesDoubleColonSelection(item, selection) && matchesFeedKeyword(item, searchValue),
    );
  }, [hasFilterRow, rawItems, selection, searchValue]);

  const feed = useMemo<FeedItem[]>(() => {
    if (!groupByTag) return filteredItems;
    const newFeedItems = [];
    for (let iter = 0; iter < filteredItems.length; iter++) {
      const item = filteredItems[iter];
      // eslint-disable-next-line no-continue
      if (!item) continue; // this case should never happen but just for sanity
      if (Array.isArray(item.tags) && item.tags.length > 0) {
        for (let tagIter = 0; tagIter < (item.tags as string[]).length; tagIter++) {
          newFeedItems.push({ ...item, tag: item.tags[tagIter], tags: [item.tags[tagIter]] });
        }
      } else {
        newFeedItems.push({ ...item, tags: [], tag: getOtherTag() });
      }
    }
    return newFeedItems as FeedItem[];
  }, [filteredItems, groupByTag, getOtherTag]);

  // Group feed items by tag if the group by tag is true
  const renderedFeedItems = useMemo<[string, FeedItem[]][]>(() => {
    if (groupByTag) {
      const feedItemsGroupedByTag = feed.reduce<{ [key: string]: FeedItem[] }>(
        (acc, item: FeedItem) => {
          const itemTag = (item.tag as string) || '';

          if (!acc[itemTag]) acc[itemTag] = [];
          acc[itemTag].push(item);

          return acc;
        },
        {} as { [key: string]: FeedItem[] },
      );

      const feedGroupedByTagSliced: [string, FeedItem[]][] = Object.entries(feedItemsGroupedByTag).map(
        ([tagKey, items]) => [tagKey, items],
      );
      const otherTag = getOtherTag();
      feedGroupedByTagSliced.sort((first, second) => {
        const firstIsOther = first[0] === otherTag;
        const secondIsOther = second[0] === otherTag;
        if (firstIsOther && !secondIsOther) return 1;
        if (!firstIsOther && secondIsOther) return -1;
        return first[0] < second[0] ? -1 : 1;
      });

      return feedGroupedByTagSliced;
    }

    return [['', feed]];
  }, [feed, getOtherTag, groupByTag]);

  const getCardVariant = () => {
    if (numberOfColumns === 2) return 'list';
    return displayType === 'feed_list' ? 'list' : 'grid';
  };

  const { participationCounts } = useParticipationCounts({ feed, feedType });

  // Display error message if the feed is not found
  if (!feed) return <div className={styles.loadingContainer}>{t('feed.unableToLoadFeed')}</div>;

  const label = translate(content.translations, 'label');
  const secondaryText = translate(content.translations, 'description');

  // An empty block still renders nothing. But once filters have narrowed a non-empty feed to zero
  // the block has to stay, so the resident can see why and clear them again.
  const isFilteredToZero = hasFilterRow && rawItems.length > 0 && filteredItems.length === 0;
  if (feed?.length === 0 && !isFilteredToZero) return null;

  return (
    <>
      <BlockHeading title={label} secondaryText={secondaryText} />
      {hasFilterRow && (
        <DoubleColonFilters
          groups={filterGroups}
          selection={selection}
          onSelect={(key, tag) => setSelection((current) => ({ ...current, [key]: tag }))}
          isFiltering={hasActiveFilters(selection, searchValue)}
          onClear={clearFilters}
          idPrefix={content.id}
        >
          <SearchBar
            key={searchInstance}
            debounceTime={300}
            placeholder={t('feed.searchPlaceholder')}
            value={searchValue}
            onChange={setSearchValue}
            className={styles.filterRowSearch}
          />
        </DoubleColonFilters>
      )}
      {isFilteredToZero ? (
        <div className={styles.noFilterResults}>{t('feed.noFilterResults')}</div>
      ) : (
        <div>
          {renderedFeedItems.map(([tag, items]) => (
            <div key={tag || 'no-tag'} className={styles.feedContainer}>
              <FeedItemsInTagGroup
                tag={tag}
                items={items}
                feedMetadata={content.metadata}
                variant={getCardVariant()}
                isTwoColumns={numberOfColumns === 2}
                participationCounts={participationCounts}
                content={content}
                isSearchHoisted={hasFilterRow}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
